// The four Summary highlights: plain sentences built from the reports, each pointing
// to where to act. Pure functions, so the wording is easy to test.
import { count, money, pct, shortRange, signedMoney, signedPct } from "./format";

const plural = (value, one, many = `${one}s`) => (Number(value) === 1 ? one : many);
const number = (value) => Number(value || 0);

function movement(change) {
  const value = number(change);
  if (Math.abs(value) < 1) return `flat (${signedPct(value)})`;
  return `${value > 0 ? "up" : "down"} ${pct(Math.abs(value))}`;
}

export function customersHighlight(health) {
  const summary = health?.summary;
  if (!summary?.regular_customers) return null;
  const flagged = number(summary.flagged);
  if (!flagged) {
    return {
      key: "customers", kicker: "Customers", tone: "good",
      text: `All ${count(summary.regular_customers)} regular customers are buying as usual.`,
      action: { label: "See customers", tab: "customers" },
    };
  }
  return {
    key: "customers", kicker: "Customers", tone: "risk",
    text: `${count(flagged)} regular ${plural(flagged, "customer is", "customers are")} behind their usual pattern: ${money(summary.at_risk_weekly)} a week at risk.`,
    action: { label: "Open the call list", tab: "customers" },
  };
}

export function openDaysHighlight(report) {
  const summary = report?.summary;
  if (!summary || summary.open_days == null) return null;
  const trend = [...(report.trend?.current || []), ...(report.trend?.previous || [])];
  const kicker = trend.some((point) => point.holiday) ? "Holidays" : "Open days";
  const action = { label: "See the trend", target: "trend" };
  const days = number(summary.open_days);
  if (!report.comparison || summary.previous_open_days == null) {
    return {
      key: "days", kicker, tone: "info", action,
      text: `${count(days)} open ${plural(days, "day")}, ${money(summary.sales_per_open_day)} per open day.`,
    };
  }
  const before = number(summary.previous_open_days);
  const dates = shortRange(report.comparison.start_date, report.comparison.end_date);
  const perDay = summary.sales_per_open_day_change_percent;
  const total = summary.sales_change_percent;
  let text = days === before
    ? `Same number of open days as ${dates} (${count(days)}).`
    : `${count(days)} open ${plural(days, "day")} vs ${count(before)} in ${dates}.`;
  if (perDay != null) {
    text += ` Per open day, sales are ${movement(perDay)}`;
    const extra = days - before;
    if (total != null && extra > 0 && number(total) > 0 && number(perDay) < number(total) / 2) {
      text += `, so most of the ${signedPct(total)} comes from the ${extra} extra open ${plural(extra, "day")}.`;
    } else if (total != null && extra < 0 && number(total) < 0 && number(perDay) > number(total) / 2) {
      text += `, so most of the ${signedPct(total)} comes from ${-extra} fewer open ${plural(-extra, "day")}.`;
    } else {
      text += ".";
    }
  }
  return { key: "days", kicker, tone: perDay != null && number(perDay) <= -1 ? "warn" : "info", text, action };
}

export function categoriesHighlight(report, health) {
  const rows = (report?.breakdowns?.product_category || []).filter((row) => row.key !== "adjustment");
  if (!rows.length) return null;
  const action = { label: "See categories", breakdown: "product_category" };
  if (!report.comparison) {
    const top = [...rows].sort((a, b) => number(b.sales) - number(a.sales))[0];
    return { key: "categories", kicker: "Products", tone: "info", action,
      text: `${top.name} leads with ${pct(top.share_percent)} of sales.` };
  }
  const down = rows.filter((row) => number(row.change_amount) < 0)
    .sort((a, b) => number(a.change_amount) - number(b.change_amount));
  if (!down.length) {
    const best = [...rows].sort((a, b) => number(b.change_amount) - number(a.change_amount))[0];
    return {
      key: "categories", kicker: "Products", tone: "good", action,
      text: number(best.change_amount) > 0
        ? `No category is down. ${best.name} grew most (${signedMoney(best.change_amount)}).`
        : "No category is down.",
    };
  }
  const worst = down[0];
  let text = down.length === 1
    ? `${worst.name} is the only category down (${signedMoney(worst.change_amount)}).`
    : `${down.length} categories are down; ${worst.name} fell most (${signedMoney(worst.change_amount)}).`;
  const stopped = (health?.rows || []).filter((row) =>
    (row.dropped_categories || []).some((item) => item.id != null && item.id === worst.id)).length;
  if (stopped) text += ` ${stopped} regular ${plural(stopped, "customer")} stopped or cut back on it.`;
  return { key: "categories", kicker: "Products", tone: "warn", text, action: { ...action, movement: "down" } };
}

const FLAG_ORDER = ["low_coverage", "unattended_risk", "losing_customers", "shrinking_accounts", "no_new_business", "no_tasks"];

function salesmanSentence(rep, medians) {
  const parts = [];
  if (rep.coverage_percent != null) {
    const median = medians?.coverage_percent;
    parts.push(`${pct(rep.coverage_percent, 0)} of their accounts ordered${median != null ? ` (team median ${pct(median, 0)})` : ""}`);
  }
  // At most one more fact, so the card stays a sentence or two; the scorecard has the rest.
  const more = [];
  if (rep.flags?.includes("losing_customers") && rep.retention_percent != null) {
    more.push(`kept ${pct(rep.retention_percent, 0)} of last period's customers`);
  }
  if (rep.flags?.includes("shrinking_accounts") && rep.existing_customer_growth_percent != null) {
    more.push(`existing customers are buying ${pct(Math.abs(number(rep.existing_customer_growth_percent)), 0)} less`);
  }
  if (rep.flags?.includes("no_new_business")) more.push("no new customers in these dates");
  if (more.length) parts.push(more[0]);
  let text = `${rep.name}: ${parts.join("; ") || `${money(rep.sales)} in sales`}.`;
  const unattended = number(rep.at_risk_without_follow_up);
  if (unattended) text += ` ${unattended} at-risk ${plural(unattended, "customer has", "customers have")} no task.`;
  return text;
}

export function salesmenHighlight(scorecard, salesman) {
  const reps = (scorecard?.rows || []).filter((row) => !row.is_office);
  if (!reps.length) return null;
  const action = { label: "Open the scorecard", tab: "salesmen" };
  const medians = scorecard.team?.medians;
  let rep = salesman != null && salesman !== "unassigned" ? reps.find((row) => row.id === salesman) : null;
  if (!rep) {
    const rank = (row) => Math.min(...(row.flags || []).map((flag) => FLAG_ORDER.indexOf(flag)).filter((index) => index >= 0), 99);
    rep = reps.filter((row) => row.flags?.length)
      .sort((a, b) => rank(a) - rank(b) || number(a.coverage_percent) - number(b.coverage_percent))[0];
  }
  if (!rep) {
    return { key: "salesmen", kicker: "Salesmen", tone: "good", action,
      text: reps.length === 1 ? `${reps[0].name} is in line with the team.` : `All ${reps.length} salesmen are in line with the team.` };
  }
  return { key: "salesmen", kicker: "Salesmen", tone: rep.flags?.length ? "warn" : "info", action,
    text: salesmanSentence(rep, medians) };
}
